import { ethers } from "ethers";

export const POLYGON_PARAMS = {
    chainId: "0x89",
    chainName: "Polygon",
    rpcUrls: ["https://polygon-rpc.com"],
    nativeCurrency: { name: "POL", symbol: "POL", decimals: 18 },
    blockExplorerUrls: ["https://polygonscan.com/"],
};

const ERC20_ABI = [
    "function approve(address spender, uint256 amount) returns (bool)",
    "function allowance(address owner, address spender) view returns (uint256)",
    "function balanceOf(address) view returns (uint256)",
    "function decimals() view returns (uint8)",
];

// Minimal ABI for AntarcticaThePortal: mint + view helpers
const PORTAL_ABI = [
    "function mint(uint256[] calldata tokenIds)",
    "function phase() view returns (uint8)",
    "function tokenPrice(uint256 id) view returns (uint256)",
    "function ownerOf(uint256 tokenId) view returns (address)",
    "event Minted(address indexed to, uint256 indexed tokenId, uint256 priceUSDC)",
];

const USDC_DECIMALS = 6;

export const hasMetamask = () => typeof window !== "undefined" && !!window.ethereum;

export async function connectWallet() {
    if (!hasMetamask()) throw new Error("MetaMask non rilevato. Installa l'estensione.");
    const accounts = await window.ethereum.request({ method: "eth_requestAccounts" });
    return accounts[0];
}

export async function ensurePolygon() {
    if (!hasMetamask()) throw new Error("MetaMask non rilevato");
    try {
        await window.ethereum.request({
            method: "wallet_switchEthereumChain",
            params: [{ chainId: POLYGON_PARAMS.chainId }],
        });
    } catch (e) {
        if (e.code === 4902) {
            await window.ethereum.request({
                method: "wallet_addEthereumChain",
                params: [POLYGON_PARAMS],
            });
        } else {
            throw e;
        }
    }
}

export async function getCurrentAccount() {
    if (!hasMetamask()) return null;
    const accounts = await window.ethereum.request({ method: "eth_accounts" });
    return accounts[0] || null;
}

async function getSigner() {
    if (!hasMetamask()) throw new Error("MetaMask non rilevato");
    await ensurePolygon();
    const provider = new ethers.BrowserProvider(window.ethereum);
    return provider.getSigner();
}

/**
 * Ensure USDC allowance for the contract is at least `requiredAmount` (in USDC units, e.g. 25 = 25 USDC).
 * Returns the approve tx hash if a new approval was sent, or null if existing allowance is enough.
 */
export async function ensureUsdcAllowance({ usdcAddress, spender, ownerAddress, requiredUsd }) {
    const signer = await getSigner();
    const usdc = new ethers.Contract(usdcAddress, ERC20_ABI, signer);
    const required = ethers.parseUnits(String(requiredUsd), USDC_DECIMALS);
    const current = await usdc.allowance(ownerAddress, spender);
    if (current >= required) return null;
    const tx = await usdc.approve(spender, required);
    const receipt = await tx.wait();
    return receipt?.hash || tx.hash;
}

/**
 * Call the on-chain mint(uint256[]) on the AntarcticaThePortal contract.
 * Returns the tx hash.
 */
export async function mintOnContract({ contractAddress, tokenIds }) {
    const signer = await getSigner();
    const contract = new ethers.Contract(contractAddress, PORTAL_ABI, signer);
    const tx = await contract.mint(tokenIds);
    const receipt = await tx.wait();
    return receipt?.hash || tx.hash;
}

/**
 * Check USDC balance (returns a number in USDC units).
 */
export async function getUsdcBalance({ usdcAddress, address }) {
    const signer = await getSigner();
    const usdc = new ethers.Contract(usdcAddress, ERC20_ABI, signer);
    const bal = await usdc.balanceOf(address);
    return parseFloat(ethers.formatUnits(bal, USDC_DECIMALS));
}

export const truncate = (a) => (a ? `${a.slice(0, 6)}…${a.slice(-4)}` : "");
